import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import HubHomeBannerCarousel from "../../components/hub/HubHomeBannerCarousel";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import HubQuickBuyRequestSection from "../../components/hub/HubQuickBuyRequestSection";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  FaArrowUp,
  FaBox,
  FaBuilding,
  FaBriefcase,
  FaCheck,
  FaChevronRight,
  FaClipboardCheck,
  FaCompass,
  FaCloudDownloadAlt,
  FaFacebookF,
  FaGavel,
  FaGoogle,
  FaIndustry,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaFileAlt,
  FaPlusCircle,
  FaBalanceScale,
  FaShoppingBag,
  FaStar,
  FaTimes,
  FaTruck,
  FaUsers,
  FaTwitter,
  FaUserFriends,
} from "react-icons/fa";
import HubFloatingRail from "../../components/hub/HubFloatingRail";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import LogisticsRfqTypeSelectionModal from "../../components/logistics/LogisticsRfqTypeSelectionModal";
import { listProcurementServicePages } from "../../constants/procurementServicePages";
import StorageService from "../../services/StorageService";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { setPostLoginRedirect } from "../../utils/authRedirect";
import { isSellerUser, resolveHubAuctionImage, resolveHubImage } from "../../utils/b2bHubHomeMedia";
import { getCurrentRole } from "../../utils/roleSwitcher";
import { getVisibleHubHomeSectionOrder, getModuleTilesConfig, getSectionGridConfig, HUB_HOME_SECTION_KEYS, resolveVisibleModuleTileKeys } from "../../constants/hubHomeSections";
import { CM_INDUSTRY_IMAGE_BY_SLUG } from "../../constants/industryTypeMap";
import { MASTER_CATEGORY_IMAGE_BY_SLUG, masterCategoryToSlug } from "../../constants/masterCategoryImageMap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/logistics_rfq_selection.scss";

const AUCTIONS_SCROLL_TARGET = "hub-active-auctions";
const CM_SCROLL_TARGET = "hub-contract-manufacturers";

const AUCTIONS_MODULE = {
  key: "auctions",
  label: "Auctions",
  icon: FaGavel,
  tone: "auctions",
  isAuction: true,
};

const CM_MODULE = {
  key: "cm",
  label: "Contract Manufacturing",
  icon: FaIndustry,
  tone: "manufacturing",
  isContractManufacturing: true,
};

const INFRA_MODULE = {
  key: "infra",
  label: "Infrastructure Leasing",
  icon: FaBuilding,
  tone: "infra",
  isInfrastructureLeasing: true,
};

const SERVICE_PROVIDERS_MODULE = {
  key: "service_providers",
  label: "Service Providers",
  icon: FaUserFriends,
  tone: "services",
  isServiceProviders: true,
};

const LOGISTICS_MODULE = {
  key: "logistics",
  label: "Logistics",
  icon: FaTruck,
  tone: "logistics",
  isLogistics: true,
};

const PROCUREMENT_MODULE = {
  key: "p2p",
  label: "Procurement-as-a-Service",
  icon: FaShoppingBag,
  tone: "procurement",
  isProcurement: true,
};

function getMarketplaceModules() {
  return [
    AUCTIONS_MODULE,
    CM_MODULE,
    INFRA_MODULE,
    SERVICE_PROVIDERS_MODULE,
    LOGISTICS_MODULE,
    PROCUREMENT_MODULE,
  ];
}
function scrollToFeaturedAuctions() {
  document.getElementById(AUCTIONS_SCROLL_TARGET)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function scrollToContractManufacturers() {
  document.getElementById(CM_SCROLL_TARGET)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function useRoleEpoch() {
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    const bump = () => setEpoch((n) => n + 1);
    window.addEventListener("storage", bump);
    return () => window.removeEventListener("storage", bump);
  }, []);
  return epoch;
}

function HubModuleTileShell({ tone, icon: Icon, label, exploreLabel, stats, onClick }) {
  const showTrend = stats?.trend_pct != null;
  const trendUp = stats?.trend_up !== false;

  return (
    <button type="button" className={`hub-module-tile hub-module-tile--${tone}`} onClick={onClick}>
      <div className="hub-module-tile__top">
        <span className={`hub-module-tile__icon hub-module-tile__icon--${tone}`} aria-hidden>
          <Icon />
        </span>
        {showTrend ? (
          <span className={`hub-module-tile__trend hub-module-tile__trend--${trendUp ? "up" : "down"}`}>
            <FaArrowUp aria-hidden />
            {trendUp ? "+" : "-"}
            {Math.abs(stats.trend_pct)}%
          </span>
        ) : null}
      </div>
      <span className="hub-module-tile__label">{label}</span>
      {stats ? (
        <div className="hub-module-tile__metrics">
          <p className="hub-module-tile__active">
            <strong>{stats.active_count ?? 0}</strong> active
          </p>
          {stats.value_label ? <p className="hub-module-tile__value">{stats.value_label}</p> : null}
        </div>
      ) : null}
      {exploreLabel ? <span className="hub-module-tile__explore">{exploreLabel}</span> : null}
    </button>
  );
}

function HubModuleActionsModal({ open, onClose, tone, titleId, title, subtitle, headerIcon: HeaderIcon, actions }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="hub-module-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={`hub-module-modal hub-module-modal--${tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="hub-module-modal__close" onClick={onClose} aria-label="Close">
          <FaTimes aria-hidden />
        </button>

        <div className="hub-module-modal__header">
          <span className="hub-module-modal__header-icon" aria-hidden>
            <HeaderIcon />
          </span>
          <div>
            <h3 id={titleId}>{title}</h3>
            <p>{subtitle}</p>
          </div>
        </div>

        <div className="hub-module-modal__actions">
          {actions.map((action) => {
            const ActionIcon = action.icon;
            const isComingSoon = Boolean(action.comingSoon);
            return (
              <button
                key={action.key}
                type="button"
                className={`hub-module-modal__action${action.primary ? " hub-module-modal__action--primary" : ""}${
                  isComingSoon ? " hub-module-modal__action--coming-soon" : ""
                }`}
                onClick={action.onClick}
                aria-disabled={isComingSoon || undefined}
              >
                <span
                  className={`hub-module-modal__action-icon hub-module-modal__action-icon--${action.iconVariant}`}
                  aria-hidden
                >
                  <ActionIcon />
                </span>
                <span className="hub-module-modal__action-text">
                  <strong>
                    {action.label}
                    {isComingSoon ? <span className="hub-module-modal__soon-badge">Coming soon</span> : null}
                  </strong>
                  <span>{action.description}</span>
                </span>
                {!isComingSoon ? (
                  <FaChevronRight className="hub-module-modal__action-arrow" aria-hidden />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

function AuctionModuleTile({ isSellerLoggedIn, isBuyerLoggedIn, stats }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const goToAuctionMarketplace = useCallback(() => {
    navigate(buildAuctionBrowseHref());
  }, [navigate]);

  const handleClick = () => {
    if (isSellerLoggedIn) {
      setModalOpen(true);
      return;
    }
    if (isBuyerLoggedIn) {
      goToAuctionMarketplace();
      return;
    }
    scrollToFeaturedAuctions();
  };

  const auctionActions = useMemo(
    () => [
      {
        key: "post",
        label: "Post Auction",
        description: "List your products for online bidding",
        icon: FaPlusCircle,
        iconVariant: "post",
        primary: true,
        onClick: () => {
          closeModal();
          navigate("/auction/create");
        },
      },
      {
        key: "explore",
        label: "Explore Auctions",
        description: "Browse live and upcoming listings",
        icon: FaCompass,
        iconVariant: "explore",
        onClick: () => {
          closeModal();
          goToAuctionMarketplace();
        },
      },
    ],
    [closeModal, goToAuctionMarketplace, navigate]
  );

  return (
    <>
      <HubModuleTileShell
        tone="auctions"
        icon={FaGavel}
        label="Auctions"
        exploreLabel={isSellerLoggedIn ? "Post or Explore →" : "Explore Auctions →"}
        stats={stats}
        onClick={handleClick}
      />
      {isSellerLoggedIn ? (
        <HubModuleActionsModal
          open={modalOpen}
          onClose={closeModal}
          tone="auctions"
          titleId="hub-auction-modal-title"
          title="Auctions"
          subtitle="Choose how you'd like to continue"
          headerIcon={FaGavel}
          actions={auctionActions}
        />
      ) : null}
    </>
  );
}

function ContractManufacturingModuleTile({ stats }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const cmActions = useMemo(
    () => [
      {
        key: "send-rfq",
        label: "Send RFQ",
        description: "Request quotes from verified contract manufacturers",
        icon: FaPaperPlane,
        iconVariant: "rfq",
        primary: true,
        onClick: () => {
          closeModal();
          navigate("/contract-manufacturers/rfq");
        },
      },
      {
        key: "explore",
        label: "Explore Manufacturers",
        description: "Browse white-label, OEM, ODM and more",
        icon: FaCompass,
        iconVariant: "explore",
        onClick: () => {
          closeModal();
          scrollToContractManufacturers();
        },
      },
    ],
    [closeModal, navigate]
  );

  return (
    <>
      <HubModuleTileShell
        tone="manufacturing"
        icon={FaIndustry}
        label="Contract Manufacturing"
        exploreLabel="Send RFQ or Explore →"
        stats={stats}
        onClick={() => setModalOpen(true)}
      />
      <HubModuleActionsModal
        open={modalOpen}
        onClose={closeModal}
        tone="manufacturing"
        titleId="hub-cm-modal-title"
        title="Contract Manufacturing"
        subtitle="Scale your production seamlessly"
        headerIcon={FaIndustry}
        actions={cmActions}
      />
    </>
  );
}

function InfrastructureLeasingModuleTile({ stats }) {
  const navigate = useNavigate();
  const roleEpoch = useRoleEpoch();
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const { tileHint, modalSubtitle, infraActions } = useMemo(() => {
    void roleEpoch;
    const currentRole = getCurrentRole();
    const isSellerHeaderRole = currentRole != null && Number(currentRole.id) === 2;
    const isBuyerHeaderRole = currentRole != null && Number(currentRole.id) === 1;

    const actions = [];

    if (!isBuyerHeaderRole) {
      actions.push({
        key: "list-asset",
        label: "List Your Asset",
        description: "List property or equipment on the marketplace",
        icon: FaPlusCircle,
        iconVariant: "list",
        primary: isSellerHeaderRole || (!isSellerHeaderRole && !isBuyerHeaderRole),
        onClick: () => {
          closeModal();
          navigate("/infrastructure-leasing/list-asset");
        },
      });
    }

    if (!isSellerHeaderRole) {
      actions.push({
        key: "post-requirement",
        label: "Post Your Requirement",
        description: "Post what you need for infrastructure and asset leasing",
        icon: FaPaperPlane,
        iconVariant: "post-req",
        primary: isBuyerHeaderRole,
        onClick: () => {
          closeModal();
          navigate("/infrastructure-leasing/post-requirement?new=1");
        },
      });
    }

    actions.push({
      key: "explore",
      label: "Explore Listings",
      description: "Browse real estate and equipment for lease",
      icon: FaCompass,
      iconVariant: "explore",
      onClick: () => {
        closeModal();
        navigate("/infrastructure-leasing/browse");
      },
    });

    let hint = "Infrastructure Leasing →";
    if (isSellerHeaderRole) hint = "List or Explore →";
    else if (isBuyerHeaderRole) hint = "Post or Explore →";

    let subtitle = "Access infrastructure without heavy investment.";
    if (isSellerHeaderRole) {
      subtitle = "List your property or equipment on the marketplace.";
    } else if (isBuyerHeaderRole) {
      subtitle = "Post what you need for infrastructure and asset leasing.";
    }

    return { tileHint: hint, modalSubtitle: subtitle, infraActions: actions };
  }, [closeModal, navigate, roleEpoch]);

  return (
    <>
      <HubModuleTileShell
        tone="infra"
        icon={FaBuilding}
        label="Infrastructure Leasing"
        exploreLabel={tileHint}
        stats={stats}
        onClick={() => setModalOpen(true)}
      />
      <HubModuleActionsModal
        open={modalOpen}
        onClose={closeModal}
        tone="infra"
        titleId="hub-infra-modal-title"
        title="Infrastructure Leasing"
        subtitle={modalSubtitle}
        headerIcon={FaBuilding}
        actions={infraActions}
      />
    </>
  );
}

function ServiceProvidersModuleTile({ stats }) {
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const showComingSoon = useCallback((label) => {
    toast.info(`${label} will be available in a future update.`);
  }, []);

  const serviceActions = useMemo(
    () => [
      {
        key: "quality-inspector",
        label: "Hire Quality Inspector",
        description: "Get reliable quality checks to reduce risks and returns",
        icon: FaClipboardCheck,
        iconVariant: "qa",
        primary: true,
        comingSoon: true,
        onClick: () => showComingSoon("Hire Quality Inspector"),
      },
      {
        key: "legal-expert",
        label: "Hire Legal Expert",
        description: "Trusted legal professionals for contracts and compliance",
        icon: FaBalanceScale,
        iconVariant: "legal",
        comingSoon: true,
        onClick: () => showComingSoon("Hire Legal Expert"),
      },
      {
        key: "consultant",
        label: "Hire Consultant",
        description: "Find the right consultant for expert advice and solutions",
        icon: FaBriefcase,
        iconVariant: "consultant",
        comingSoon: true,
        onClick: () => showComingSoon("Hire Consultant"),
      },
    ],
    [showComingSoon]
  );

  return (
    <>
      <HubModuleTileShell
        tone="services"
        icon={FaUserFriends}
        label="Service Providers"
        exploreLabel="Hire a professional →"
        stats={stats}
        onClick={() => setModalOpen(true)}
      />
      <HubModuleActionsModal
        open={modalOpen}
        onClose={closeModal}
        tone="services"
        titleId="hub-services-modal-title"
        title="Service Providers"
        subtitle="Find the right expert for your business"
        headerIcon={FaUserFriends}
        actions={serviceActions}
      />
    </>
  );
}

function LogisticsModuleTile({ stats }) {
  const navigate = useNavigate();
  const [hubModalOpen, setHubModalOpen] = useState(false);
  const [rfqModalOpen, setRfqModalOpen] = useState(false);
  const closeHubModal = useCallback(() => setHubModalOpen(false), []);
  const closeRfqModal = useCallback(() => setRfqModalOpen(false), []);

  const logisticsActions = useMemo(
    () => [
      {
        key: "send-rfq",
        label: "Send RFQ",
        description: "Submit transport requirements and receive quotes from verified providers",
        icon: FaPaperPlane,
        iconVariant: "rfq",
        primary: true,
        onClick: () => {
          closeHubModal();
          setRfqModalOpen(true);
        },
      },
      {
        key: "explore",
        label: "Explore Providers",
        description: "Browse verified logistics and delivery partners",
        icon: FaCompass,
        iconVariant: "explore",
        onClick: () => {
          closeHubModal();
          navigate("/logistics-providers");
        },
      },
    ],
    [closeHubModal, navigate]
  );

  return (
    <>
      <HubModuleTileShell
        tone="logistics"
        icon={FaTruck}
        label="Logistics"
        exploreLabel="Send RFQ or Explore →"
        stats={stats}
        onClick={() => setHubModalOpen(true)}
      />
      <HubModuleActionsModal
        open={hubModalOpen}
        onClose={closeHubModal}
        tone="logistics"
        titleId="hub-logistics-modal-title"
        title="Logistics"
        subtitle="Submit your transport requirements and compare quotes from multiple providers"
        headerIcon={FaTruck}
        actions={logisticsActions}
      />
      <LogisticsRfqTypeSelectionModal
        open={rfqModalOpen}
        onClose={closeRfqModal}
        title="Send Logistics RFQ"
        introText="Choose the type of logistics service that matches your requirement."
      />
    </>
  );
}

function ProcurementModuleTile({ stats }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const procurementActions = useMemo(() => {
    const servicePages = listProcurementServicePages();
    const iconBySlug = {
      "supplier-discovery": FaFileAlt,
      "managed-procurement": FaShoppingBag,
      "procurement-retainer": FaUsers,
    };
    const actions = servicePages.map((page, index) => ({
      key: page.slug,
      label: page.package.title,
      description: page.hero.description,
      icon: iconBySlug[page.slug] || FaShoppingBag,
      iconVariant: page.slug.replace(/-/g, "_"),
      primary: index === 1,
      onClick: () => {
        closeModal();
        navigate(`/procurement-services/${page.slug}`);
      },
    }));
    actions.push({
      key: "all-services",
      label: "View all procurement services",
      description: "Explore packages, process, and AI-assisted procurement intelligence",
      icon: FaCompass,
      iconVariant: "explore",
      onClick: () => {
        closeModal();
        navigate("/procurement-services");
      },
    });
    return actions;
  }, [closeModal, navigate]);

  return (
    <>
      <HubModuleTileShell
        tone="procurement"
        icon={FaShoppingBag}
        label="Procurement-as-a-Service"
        exploreLabel="Available procurement services →"
        stats={stats}
        onClick={() => setModalOpen(true)}
      />
      <HubModuleActionsModal
        open={modalOpen}
        onClose={closeModal}
        tone="procurement"
        titleId="hub-procurement-modal-title"
        title="Available Procurement Services"
        subtitle="Choose a service to learn more and submit a procurement request"
        headerIcon={FaShoppingBag}
        actions={procurementActions}
      />
    </>
  );
}

function MarketplaceModuleTile({ module: mod, stats }) {
  const Icon = mod.icon;
  const className = `hub-module-tile hub-module-tile--${mod.tone}`;

  if (mod.scrollTarget) {
    return (
                  <button
                    type="button"
        className={className}
        onClick={() => {
          document.getElementById(mod.scrollTarget)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      >
        <HubModuleTileInner tone={mod.tone} icon={Icon} label={mod.label} exploreLabel={mod.exploreLabel} stats={stats} />
                  </button>
    );
  }

  return (
    <Link to={mod.to} className={className}>
      <HubModuleTileInner tone={mod.tone} icon={Icon} label={mod.label} exploreLabel={mod.exploreLabel} stats={stats} />
                      </Link>
  );
}

function HubModuleTileInner({ tone, icon: Icon, label, exploreLabel, stats }) {
  const showTrend = stats?.trend_pct != null;
  const trendUp = stats?.trend_up !== false;

  return (
    <>
      <div className="hub-module-tile__top">
        <span className={`hub-module-tile__icon hub-module-tile__icon--${tone}`} aria-hidden>
          <Icon />
        </span>
        {showTrend ? (
          <span className={`hub-module-tile__trend hub-module-tile__trend--${trendUp ? "up" : "down"}`}>
            <FaArrowUp aria-hidden />
            {trendUp ? "+" : "-"}
            {Math.abs(stats.trend_pct)}%
          </span>
        ) : null}
              </div>
      <span className="hub-module-tile__label">{label}</span>
      {stats ? (
        <div className="hub-module-tile__metrics">
          <p className="hub-module-tile__active">
            <strong>{stats.active_count ?? 0}</strong> active
          </p>
          {stats.value_label ? <p className="hub-module-tile__value">{stats.value_label}</p> : null}
            </div>
      ) : null}
      {exploreLabel ? <span className="hub-module-tile__explore">{exploreLabel}</span> : null}
    </>
  );
}

function HubCardImage({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`hub-card__image hub-card__image--placeholder ${className || ""}`.trim()} aria-hidden>
        <FaBox />
                      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`hub-card__image ${className || ""}`.trim()}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function HubLogisticsServiceCard({ item }) {
  return (
    <Link to={item.href} className="hub-card hub-card--logistics">
      <div className="hub-card__media">
        <HubCardImage src={resolveHubImage(item.image_path)} alt={item.title} />
        {item.verified ? <span className="hub-card__badge hub-card__badge--blue">Verified</span> : null}
              </div>
      <div className="hub-card__body">
        <div className="hub-card__title-row hub-card__title-row--cm">
          <h3>{item.title}</h3>
          {item.rating_label ? (
            <span className="hub-card__rating">
              <FaStar aria-hidden /> {item.rating_label}
            </span>
          ) : null}
            </div>
        {item.company_type_label ? (
          <p className="hub-card__specialization">{item.company_type_label}</p>
        ) : (
          <p className="hub-card__specialization">Logistics &amp; Delivery</p>
        )}
        {item.location ? (
          <p className="hub-card__meta">
            <FaMapMarkerAlt aria-hidden /> {item.location}
          </p>
        ) : null}
        <div className="hub-card__cm-metrics">
          <p className="hub-card__cm-metric">
            <span>Modes:</span>
            <strong>{item.modes_label || "—"}</strong>
          </p>
          <p className="hub-card__cm-metric">
            <span>Experience:</span>
            <strong>{item.experience_label || "—"}</strong>
          </p>
                    </div>
        <div className="hub-card__cm-min-order">
          <span>Coverage:</span>
          <strong>{item.coverage_label || "Pan-India"}</strong>
                  </div>
        <div className="hub-card__cert-bar hub-card__cert-bar--service">Available for RFQ</div>
              </div>
    </Link>
  );
}

function HubInfrastructureServiceCard({ item }) {
  return (
    <Link to={item.href} className="hub-card hub-card--infra-service">
      <div className="hub-card__media">
        <HubCardImage src={resolveHubImage(item.image_path)} alt={item.title} />
        {item.transaction_label ? (
          <span className="hub-card__tx-badge">{item.transaction_label}</span>
        ) : null}
      </div>
      <div className="hub-card__body">
        <h3>{item.title}</h3>
        {item.category_label ? (
          <p className="hub-card__specialization">{item.category_label}</p>
        ) : null}
        {item.location ? (
          <div className="hub-card__location-row hub-card__location-row--infra">
            <FaMapMarkerAlt aria-hidden />
            <p>{item.location}</p>
          </div>
        ) : null}
        {item.key_spec_label ? <p className="hub-card__infra-spec">{item.key_spec_label}</p> : null}
        <div className="hub-card__infra-footer">
          <div className="hub-card__infra-price">
            <span>Price:</span>
            <span className="hub-card__infra-price-value">{item.price_label || "On request"}</span>
          </div>
          <p className="hub-card__cert-badge">{item.availability_label || "Available"}</p>
        </div>
      </div>
    </Link>
  );
}

function SectionHeader({ title, viewMoreTo, viewMoreLabel = "See More" }) {
  return (
    <div className="hub-section__head">
      <h2 className="hub-section__title">{title}</h2>
      {viewMoreTo ? (
        <Link to={viewMoreTo} className="hub-section__more">
          {viewMoreLabel} <FaChevronRight aria-hidden />
            </Link>
      ) : null}
          </div>
  );
}

function useAuctionCountdown(auctions) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return useMemo(() => {
    return (auctions || []).map((a) => {
      if (!a.end_date) return a.time_left_label;
      const ms = Math.max(0, new Date(a.end_date).getTime() - now);
      const totalSec = Math.floor(ms / 1000);
      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      if (days > 0) return `${days}d ${hours}h`;
      if (hours > 0) return `${hours}h ${mins}m`;
      if (mins > 0) return `${mins}m`;
      return "<1m";
    });
  }, [auctions, now]);
}

function buildProductBrowseHref(categoryId, subCategoryId, subSubCategoryId) {
  const params = new URLSearchParams();
  if (categoryId) params.set("category", String(categoryId));
  if (subCategoryId) params.set("subCategory", String(subCategoryId));
  if (subSubCategoryId) params.set("subSubCategory", String(subSubCategoryId));
  const qs = params.toString();
  return qs ? `/marketplace/products?${qs}` : "/marketplace/products";
}

function buildCmBrowseHref() {
  return "/marketplace/products?module=cm";
}

function buildAuctionBrowseHref() {
  return "/marketplace/products?module=auctions";
}

function buildInfraBrowseHref() {
  return "/marketplace/products?module=infra";
}

function HubActiveAuctionsSection({ auctions, categories, loading, displayConfig }) {
  const rows = displayConfig?.rows ?? 2;
  const itemsPerRow = displayConfig?.products_per_row ?? 4;
  const maxItems = displayConfig?.total_items ?? rows * itemsPerRow;

  const [categoryId, setCategoryId] = useState(null);

  const visibleCategories = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    const withAuctions = list.filter((c) => (c.auction_count ?? 0) > 0);
    return withAuctions.length ? withAuctions : list;
  }, [categories]);

  const filteredAuctions = useMemo(() => {
    const list = Array.isArray(auctions) ? auctions : [];
    let filtered = list;
    if (categoryId) {
      filtered = filtered.filter((item) => Number(item.category_id) === Number(categoryId));
    }
    return filtered.slice(0, maxItems);
  }, [auctions, categoryId, maxItems]);

  const countdowns = useAuctionCountdown(filteredAuctions);
  const viewMoreHref = buildAuctionBrowseHref();

  return (
    <section id="hub-active-auctions" className="hub-section hub-section--auctions">
      <SectionHeader title="Active Auctions" viewMoreTo={viewMoreHref} />

      <div className="hub-category-nav" role="navigation" aria-label="Auction categories">
        <div className="hub-category-nav__row">
          <button
            type="button"
            className={`hub-category-pill${categoryId == null ? " hub-category-pill--active" : ""}`}
            onClick={() => setCategoryId(null)}
          >
            All
          </button>
          {visibleCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`hub-category-pill${categoryId === cat.id ? " hub-category-pill--active" : ""}`}
              onClick={() => setCategoryId(cat.id)}
            >
              {cat.name}
              {cat.auction_count > 0 ? (
                <span className="hub-category-pill__count">{cat.auction_count}</span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div
        className="hub-card-grid"
        style={{ gridTemplateColumns: `repeat(${itemsPerRow}, minmax(0, 1fr))` }}
      >
        {filteredAuctions.map((item, idx) => (
          <Link key={item.id} to={item.href} className="hub-card hub-card--auction">
            <div className="hub-card__media">
              <HubCardImage src={resolveHubAuctionImage(item.image_path, item.id)} alt={item.title} />
              <span className="hub-card__countdown-badge">
                {countdowns[idx] || item.time_left_label || "—"}
              </span>
            </div>
            <div className="hub-card__body">
              <h3>{item.title}</h3>
              <div className="hub-card__location-row hub-card__location-row--auction">
                {item.location ? (
                  <>
                    <FaMapMarkerAlt aria-hidden />
                    <p>{item.location}</p>
                  </>
                ) : null}
                {item.condition_label ? (
                  <span className="hub-card__condition-badge">{item.condition_label}</span>
                ) : null}
              </div>
              <div className="hub-card__auction-stats">
                <div className="hub-card__auction-stat">
                  <span>Current Bid:</span>
                  <strong className="hub-card__auction-stat--bid">
                    {item.current_bid_label || "—"}
                  </strong>
                </div>
                <div className="hub-card__auction-stat">
                  <span>Bidders:</span>
                  <strong>{item.bidder_count ?? 0}</strong>
                </div>
                <div className="hub-card__auction-stat">
                  <span>Ends:</span>
                  <strong className="hub-card__auction-stat--ends">
                    {item.ends_label || "—"}
                  </strong>
                </div>
              </div>
              <div className="hub-card__auction-footer">
                <span className="hub-card__auction-deposit">
                  {item.deposit_label ? `Deposit: ${item.deposit_label}` : "Deposit: —"}
                </span>
                <span className="hub-card__auction-status">
                  <FaCheck aria-hidden /> {item.availability_label || "Available"}
                </span>
              </div>
            </div>
          </Link>
        ))}
        {!loading && filteredAuctions.length === 0 ? (
          <p className="hub-empty">
            {categoryId ? "No active auctions in this category yet." : "No active auctions right now."}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function buildCmIndustryHref(slug) {
  return slug ? `/contract-manufacturers/${encodeURIComponent(slug)}` : "/contract-manufacturers";
}

function HubTopIndustriesSection({ industries, loading, displayConfig }) {
  const config = displayConfig || { total_items: 10, products_per_row: 5 };
  const items = useMemo(() => {
    const list = Array.isArray(industries) ? industries : [];
    return list.slice(0, config.total_items);
  }, [industries, config.total_items]);

  if (!loading && !items.length) return null;

  return (
    <section id="hub-top-industries" className="hub-section hub-section--top-industries">
      <SectionHeader
        title="Top Industries"
        viewMoreTo="/contract-manufacturers"
        viewMoreLabel="View All Industries"
      />
      <div
        className="hub-trading-cat-grid hub-top-industries-grid"
        style={{ gridTemplateColumns: `repeat(${config.products_per_row}, minmax(0, 1fr))` }}
      >
        {items.map((industry) => {
          const imagePath = industry.image_path || CM_INDUSTRY_IMAGE_BY_SLUG[industry.slug];
          const imageSrc = imagePath ? resolveHubImage(imagePath) : null;
          const stats = [
            industry.count > 0 ? `${industry.count} Manufacturer${industry.count === 1 ? "" : "s"}` : null,
            industry.product_count > 0 ? `${industry.product_count} Product${industry.product_count === 1 ? "" : "s"}` : null,
            industry.rfq_count > 0 ? `${industry.rfq_count} Active RFQ${industry.rfq_count === 1 ? "" : "s"}` : null,
            industry.verified_count > 0 ? `${industry.verified_count} Verified` : null,
          ].filter(Boolean);

          return (
            <article key={industry.slug} className="hub-trading-cat-card hub-top-industry-card">
              <Link to={buildCmIndustryHref(industry.slug)} className="hub-trading-cat-card__media-link">
                <div className={`hub-trading-cat-card__media${imageSrc ? " hub-trading-cat-card__media--has-image" : ""}`}>
                  {imageSrc ? (
                    <img src={imageSrc} alt={industry.name} loading="lazy" decoding="async" />
                  ) : (
                    <div className="hub-trading-cat-card__media-fallback" aria-hidden>
                      <FaIndustry />
                    </div>
                  )}
                </div>
              </Link>
              <div className="hub-trading-cat-card__body">
                <h3 className="hub-trading-cat-card__title">
                  <Link to={buildCmIndustryHref(industry.slug)} className="hub-trading-cat-card__title-link">
                    {industry.name}
                  </Link>
                </h3>
                {stats.length ? (
                  <ul className="hub-trading-cat-card__subs">
                    {stats.slice(0, 4).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : null}
                <Link to={buildCmIndustryHref(industry.slug)} className="hub-trading-cat-card__view-all">
                  View All <FaChevronRight aria-hidden />
                </Link>
              </div>
            </article>
          );
        })}
        {loading && !items.length ? (
          <p className="hub-empty">Loading top industries…</p>
        ) : null}
      </div>
    </section>
  );
}

function HubContractManufacturersSection({ manufacturers, industries, loading, displayConfig }) {
  const rows = displayConfig?.rows ?? 2;
  const itemsPerRow = displayConfig?.products_per_row ?? 4;
  const maxItems = displayConfig?.total_items ?? rows * itemsPerRow;

  const [industrySlug, setIndustrySlug] = useState(null);

  const visibleIndustries = useMemo(() => {
    const list = Array.isArray(industries) ? industries : [];
    const withManufacturers = list.filter((t) => (t.count ?? 0) > 0);
    return withManufacturers.length ? withManufacturers : list;
  }, [industries]);

  const filteredManufacturers = useMemo(() => {
    const list = Array.isArray(manufacturers) ? manufacturers : [];
    let filtered = list;
    if (industrySlug) {
      filtered = filtered.filter(
        (item) =>
          Array.isArray(item.industry_slugs) && item.industry_slugs.includes(industrySlug),
      );
    }
    return filtered.slice(0, maxItems);
  }, [manufacturers, industrySlug, maxItems]);

  const viewMoreHref = buildCmBrowseHref();

  return (
    <section id={CM_SCROLL_TARGET} className="hub-section hub-section--cm">
      <SectionHeader title="Contract Manufacturers" viewMoreTo={viewMoreHref} />

      <div className="hub-category-nav" role="navigation" aria-label="Contract manufacturer industries">
        <div className="hub-category-nav__row">
          <button
            type="button"
            className={`hub-category-pill${industrySlug == null ? " hub-category-pill--active" : ""}`}
            onClick={() => setIndustrySlug(null)}
          >
            All
          </button>
          {visibleIndustries.map((industryOption) => (
            <button
              key={industryOption.slug}
              type="button"
              className={`hub-category-pill${industrySlug === industryOption.slug ? " hub-category-pill--active" : ""}`}
              onClick={() => setIndustrySlug(industryOption.slug)}
            >
              {industryOption.name}
              {industryOption.count > 0 ? (
                <span className="hub-category-pill__count">{industryOption.count}</span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div
        className={[
          "hub-card-grid",
          filteredManufacturers.length > 0 && filteredManufacturers.length < itemsPerRow
            ? "hub-card-grid--auto-fill"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={
          filteredManufacturers.length >= itemsPerRow
            ? { gridTemplateColumns: `repeat(${itemsPerRow}, minmax(0, 1fr))` }
            : undefined
        }
      >
        {filteredManufacturers.map((item) => (
          <Link key={item.id} to={item.href} className="hub-card hub-card--cm">
            <div className="hub-card__media">
              <HubCardImage src={resolveHubImage(item.image_path)} alt={item.title} />
            </div>
            <div className="hub-card__body">
              <div className="hub-card__title-row hub-card__title-row--cm">
                <h3>{item.title}</h3>
                {item.rating_label ? (
                  <span className="hub-card__rating hub-card__rating--cm">
                    <FaStar aria-hidden /> {item.rating_label}
                  </span>
                ) : null}
              </div>
              {item.specialization ? (
                <p className="hub-card__specialization">{item.specialization}</p>
              ) : null}
              {item.location ? (
                <div className="hub-card__location-row hub-card__location-row--cm">
                  <FaMapMarkerAlt aria-hidden />
                  <p>{item.location}</p>
                </div>
              ) : null}
              <div className="hub-card__cm-metrics">
                {item.capacity_label ? <p>Capacity: {item.capacity_label}</p> : null}
                {item.lead_time_label ? <p>Lead Time: {item.lead_time_label}</p> : null}
              </div>
              <div className="hub-card__cm-footer">
                <div className="hub-card__cm-min-order">
                  <span>Min Order:</span>
                  <span className="hub-card__cm-min-order-value">
                    {item.min_order_price_label || item.min_order_label || "—"}
                  </span>
                </div>
                {item.certifications_label ? (
                  <p className="hub-card__cert-badge">{item.certifications_label}</p>
                ) : null}
              </div>
            </div>
          </Link>
        ))}
        {!loading && filteredManufacturers.length === 0 ? (
          <p className="hub-empty hub-empty--section">
            {industrySlug ? "No contract manufacturers in this industry yet." : "No contract manufacturers listed yet."}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function HubTradingCategoriesSection({ categories, loading, displayConfig }) {
  const rows = displayConfig?.rows ?? 2;
  const categoriesPerRow = displayConfig?.products_per_row ?? 5;
  const maxCategories = displayConfig?.total_items ?? rows * categoriesPerRow;
  const items = useMemo(
    () => (Array.isArray(categories) ? categories : []).slice(0, maxCategories),
    [categories, maxCategories],
  );

  return (
    <section id="hub-trading-categories" className="hub-section hub-section--trading-categories">
      <SectionHeader
        title="Trending Categories"
        viewMoreTo="/marketplace/products"
        viewMoreLabel="View All Categories"
      />
      <div
        className="hub-trading-cat-grid"
        style={{ gridTemplateColumns: `repeat(${categoriesPerRow}, minmax(0, 1fr))` }}
      >
        {items.map((category) => {
          const slug = category.slug || masterCategoryToSlug(category.name);
          const imagePath =
            category.image_path || MASTER_CATEGORY_IMAGE_BY_SLUG[slug] || null;
          const imageSrc = imagePath ? resolveHubImage(imagePath) : null;
          const subcategories = Array.isArray(category.subcategories) ? category.subcategories.slice(0, 4) : [];

          return (
            <article key={category.id} className="hub-trading-cat-card">
              <Link to={buildProductBrowseHref(category.id)} className="hub-trading-cat-card__media-link">
                <div className={`hub-trading-cat-card__media${imageSrc ? " hub-trading-cat-card__media--has-image" : ""}`}>
                  {imageSrc ? (
                    <img src={imageSrc} alt={category.name} loading="lazy" decoding="async" />
                  ) : (
                    <div className="hub-trading-cat-card__media-fallback" aria-hidden>
                      <FaBox />
                    </div>
                  )}
                </div>
              </Link>
              <div className="hub-trading-cat-card__body">
                <h3 className="hub-trading-cat-card__title">
                  <Link to={buildProductBrowseHref(category.id)} className="hub-trading-cat-card__title-link">
                    {category.name}
                  </Link>
                </h3>
                {subcategories.length ? (
                  <ul className="hub-trading-cat-card__subs">
                    {subcategories.map((sub) => (
                      <li key={sub.id}>
                        <Link
                          to={buildProductBrowseHref(category.id, sub.id)}
                          className="hub-trading-cat-card__sub-link"
                        >
                          {sub.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <Link to={buildProductBrowseHref(category.id)} className="hub-trading-cat-card__view-all">
                  View All <FaChevronRight aria-hidden />
                </Link>
              </div>
            </article>
          );
        })}
        {!loading && !items.length ? (
          <p className="hub-empty">No trading categories available yet.</p>
        ) : null}
      </div>
    </section>
  );
}

function HubFeaturedCategoryShowcaseSection({ showcase, displayConfig }) {
  if (!showcase) return null;

  const cols = displayConfig?.products_per_row ?? 4;
  const categoryImage = showcase?.image_path ? resolveHubImage(showcase.image_path) : null;
  const subcategories = Array.isArray(showcase?.subcategories) ? showcase.subcategories : [];

  return (
    <section id="hub-featured-category-showcase" className="hub-section hub-section--featured-showcase">
      <div className="hub-featured-showcase">
        <aside className="hub-featured-showcase__aside">
          <h2 className="hub-featured-showcase__title">
            <Link to={buildProductBrowseHref(showcase.id)} className="hub-featured-showcase__title-link">
              {showcase.name}
            </Link>
          </h2>
          {showcase.description ? (
            <p className="hub-featured-showcase__desc">{showcase.description}</p>
          ) : null}
          {categoryImage ? (
            <Link to={buildProductBrowseHref(showcase.id)} className="hub-featured-showcase__hero-link">
              <img
                src={categoryImage}
                alt={showcase.name}
                className="hub-featured-showcase__hero-img"
                loading="lazy"
                decoding="async"
              />
            </Link>
          ) : (
            <div className="hub-featured-showcase__hero-fallback" aria-hidden>
              <FaBox />
            </div>
          )}
        </aside>
        <div
          className="hub-featured-showcase__grid"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {subcategories.map((sub) => {
            const subImage = sub.image_path ? resolveHubImage(sub.image_path) : null;
            return (
              <Link
                key={sub.id}
                to={buildProductBrowseHref(showcase.id, sub.id)}
                className="hub-featured-showcase__item"
              >
                <div className={`hub-featured-showcase__item-media${subImage ? " hub-featured-showcase__item-media--has-image" : ""}`}>
                  {subImage ? (
                    <img src={subImage} alt={sub.name} loading="lazy" decoding="async" />
                  ) : (
                    <FaBox aria-hidden />
                  )}
                </div>
                <span className="hub-featured-showcase__item-label">{sub.name}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function formatSellerProductPrice(item) {
  return item?.price_label || null;
}

function HubSellerProductsSection({ products, loading, searchQuery = "", displayConfig }) {
  const rows = displayConfig?.rows ?? 2;
  const productsPerRow = displayConfig?.products_per_row ?? 4;
  const maxProducts = rows * productsPerRow;

  const recentProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const needle = String(searchQuery || "").trim().toLowerCase();
    let filtered = list;
    if (needle) {
      filtered = filtered.filter((p) => {
        const haystack = [p.title, p.company_name, p.location, p.badge]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(needle);
      });
    }
    return filtered.slice(0, maxProducts);
  }, [products, searchQuery, maxProducts]);

  const viewMoreHref = buildProductBrowseHref();

  return (
    <section id="hub-seller-products" className="hub-section hub-section--seller-products">
      <SectionHeader title="Seller Products" viewMoreTo={viewMoreHref} />

      <div
        className="hub-card-grid"
        style={{ gridTemplateColumns: `repeat(${productsPerRow}, minmax(0, 1fr))` }}
      >
        {recentProducts.map((item) => {
          const priceText = formatSellerProductPrice(item);
          return (
          <Link
            key={item.id}
            to={item.details_href || `/marketplace/products/${item.id}`}
            className="hub-card hub-card--product"
          >
            <div className="hub-card__media">
              <HubCardImage src={resolveHubImage(item.image_path)} alt={item.title} />
            </div>
            <div className="hub-card__body">
              <div className="hub-card__title-row hub-card__title-row--product">
                <h3>{item.title}</h3>
                {item.badge ? <span className="hub-card__inline-badge">{item.badge}</span> : null}
              </div>
              {item.company_name ? <p className="hub-card__company">{item.company_name}</p> : null}
              {item.location ? (
                <div className="hub-card__location-row hub-card__location-row--product">
                  <FaMapMarkerAlt aria-hidden />
                  <p>{item.location}</p>
                </div>
              ) : null}
              <div className="hub-card__product-pricing">
                {priceText ? <p className="hub-card__price">{priceText}</p> : null}
                {item.moq_label ? <p className="hub-card__moq">{item.moq_label}</p> : null}
              </div>
            </div>
                    </Link>
          );
        })}
        {!loading && recentProducts.length === 0 ? (
          <p className="hub-empty">
            {searchQuery.trim()
              ? `No products match "${searchQuery.trim()}".`
              : "No seller products yet."}
          </p>
        ) : null}
        </div>
      </section>
  );
}

export default function Home() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const productSearchQuery = searchParams.get("q") || "";
  const loginReturnTo = `${location.pathname}${location.search}`;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [user, setUser] = useState(() => StorageService.getUser());
  const [isAuthed, setIsAuthed] = useState(() => StorageService.isAuthenticated());
  const isSellerLoggedIn = isAuthed && isSellerUser(user);
  const isBuyerLoggedIn = useMemo(() => {
    if (!isAuthed) return false;
    const currentRole = getCurrentRole();
    return currentRole != null && Number(currentRole.id) === 1;
  }, [isAuthed, user]);
  const marketplaceModules = useMemo(() => getMarketplaceModules(), []);
  const visibleModuleTileKeys = useMemo(
    () => resolveVisibleModuleTileKeys(getModuleTilesConfig(data?.home_sections)),
    [data?.home_sections],
  );
  const visibleMarketplaceModules = useMemo(() => {
    const byKey = new Map(marketplaceModules.map((mod) => [mod.key, mod]));
    return visibleModuleTileKeys.map((key) => byKey.get(key)).filter(Boolean);
  }, [marketplaceModules, visibleModuleTileKeys]);

  useEffect(() => {
    setPostLoginRedirect(loginReturnTo);
  }, [loginReturnTo]);

  useEffect(() => {
    const syncAuth = () => {
      setUser(StorageService.getUser());
      setIsAuthed(StorageService.isAuthenticated());
    };
    window.addEventListener("storage", syncAuth);
    window.addEventListener("focus", syncAuth);
    return () => {
      window.removeEventListener("storage", syncAuth);
      window.removeEventListener("focus", syncAuth);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    b2bHubHomeService
      .getHome()
      .then((res) => {
        if (!cancelled) setData(res?.data ?? res ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load hub dashboard");
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);


  const hero = data?.hero;
  const homeBanners = data?.home_banners;
  const sponsoredBanners = data?.sponsored_banners;
  const promoBanners = data?.promo_banners;
  const moduleStats = data?.module_stats || {};
  const visibleSectionKeys = useMemo(
    () => getVisibleHubHomeSectionOrder(data?.home_sections),
    [data?.home_sections],
  );

  const sectionGridConfigs = useMemo(() => {
    const sections = data?.home_sections;
    return {
      [HUB_HOME_SECTION_KEYS.SELLER_PRODUCTS]: getSectionGridConfig(sections, HUB_HOME_SECTION_KEYS.SELLER_PRODUCTS),
      [HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES]: getSectionGridConfig(
        sections,
        HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES,
      ),
      [HUB_HOME_SECTION_KEYS.FEATURED_CATEGORY_SHOWCASE]: getSectionGridConfig(
        sections,
        HUB_HOME_SECTION_KEYS.FEATURED_CATEGORY_SHOWCASE,
      ),
      [HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES]: getSectionGridConfig(
        sections,
        HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES,
      ),
      [HUB_HOME_SECTION_KEYS.CONTRACT_MANUFACTURERS]: getSectionGridConfig(
        sections,
        HUB_HOME_SECTION_KEYS.CONTRACT_MANUFACTURERS,
      ),
      [HUB_HOME_SECTION_KEYS.ACTIVE_AUCTIONS]: getSectionGridConfig(sections, HUB_HOME_SECTION_KEYS.ACTIVE_AUCTIONS),
      [HUB_HOME_SECTION_KEYS.INFRASTRUCTURE_LEASING]: getSectionGridConfig(
        sections,
        HUB_HOME_SECTION_KEYS.INFRASTRUCTURE_LEASING,
      ),
    };
  }, [data?.home_sections]);

  const topIndustriesItems = useMemo(() => {
    const configured = Array.isArray(data?.top_industries) ? data.top_industries : [];
    if (configured.length) return configured;

    const limit = sectionGridConfigs[HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES]?.total_items ?? 10;
    const fallback = Array.isArray(data?.contract_manufacturer_industries)
      ? data.contract_manufacturer_industries
      : [];

    return [...fallback]
      .filter((industry) => (industry.count ?? 0) > 0)
      .sort(
        (a, b) =>
          (b.count ?? 0) - (a.count ?? 0) ||
          (b.product_count ?? 0) - (a.product_count ?? 0) ||
          String(a.name).localeCompare(String(b.name)),
      )
      .slice(0, limit);
  }, [data?.top_industries, data?.contract_manufacturer_industries, sectionGridConfigs]);

  const renderHomeSection = (sectionKey) => {
    switch (sectionKey) {
      case HUB_HOME_SECTION_KEYS.HERO_BANNERS:
        if (homeBanners?.length) {
          return (
            <HubHomeBannerCarousel
              key={sectionKey}
              banners={homeBanners}
              bannerSlot="home"
              hideBlurredBackground={false}
              ariaLabel="Home banner slider"
            />
          );
        }
        if (hero) {
          return (
            <section key={sectionKey} className="hub-hero-banner">
              <div className="hub-hero-banner__content">
                <h2>{hero.title}</h2>
                <p>{hero.subtitle}</p>
                <Link to={hero.cta_href || "/signup"} className="hub-btn hub-btn--white">
                  {hero.cta_label}
                </Link>
              </div>
              <div className="hub-hero-banner__stats">
                {(hero.stats || []).map((s) => (
                  <div key={s.label} className="hub-hero-banner__stat">
                    <strong>{s.value}</strong>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            </section>
          );
        }
        return null;

      case HUB_HOME_SECTION_KEYS.MODULE_TILES:
        if (!visibleMarketplaceModules.length) return null;
        return (
          <section key={sectionKey} className="hub-module-row" aria-label="Marketplace modules">
            {visibleMarketplaceModules.map((mod) => {
              const stats = moduleStats[mod.key];
              if (mod.isAuction) {
                return (
                  <AuctionModuleTile
                    key={mod.key}
                    isSellerLoggedIn={isSellerLoggedIn}
                    isBuyerLoggedIn={isBuyerLoggedIn}
                    stats={stats}
                  />
                );
              }
              if (mod.isContractManufacturing) {
                return <ContractManufacturingModuleTile key={mod.key} stats={stats} />;
              }
              if (mod.isInfrastructureLeasing) {
                return <InfrastructureLeasingModuleTile key={mod.key} stats={stats} />;
              }
              if (mod.isServiceProviders) {
                return <ServiceProvidersModuleTile key={mod.key} stats={stats} />;
              }
              if (mod.isLogistics) {
                return <LogisticsModuleTile key={mod.key} stats={stats} />;
              }
              if (mod.isProcurement) {
                return <ProcurementModuleTile key={mod.key} stats={stats} />;
              }
              return <MarketplaceModuleTile key={mod.key} module={mod} stats={stats} />;
            })}
          </section>
        );

      case HUB_HOME_SECTION_KEYS.SELLER_PRODUCTS:
        return (
          <HubSellerProductsSection
            key={sectionKey}
            products={data?.seller_products}
            loading={loading}
            searchQuery={productSearchQuery}
            displayConfig={sectionGridConfigs[HUB_HOME_SECTION_KEYS.SELLER_PRODUCTS]}
          />
        );

      case HUB_HOME_SECTION_KEYS.SPONSORED_BANNERS:
        return sponsoredBanners?.length ? (
          <div key={sectionKey} className="hub-section hub-section--banner-strip">
            <HubHomeBannerCarousel
              banners={sponsoredBanners}
              bannerSlot="mid"
              ariaLabel="Mid page banners"
            />
          </div>
        ) : null;

      case HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES:
        return (
          <HubTradingCategoriesSection
            key={sectionKey}
            categories={data?.trading_categories}
            loading={loading}
            displayConfig={sectionGridConfigs[HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES]}
          />
        );

      case HUB_HOME_SECTION_KEYS.FEATURED_CATEGORY_SHOWCASE:
        return (
          <HubFeaturedCategoryShowcaseSection
            key={sectionKey}
            showcase={data?.featured_category_showcase}
            loading={loading}
            displayConfig={sectionGridConfigs[HUB_HOME_SECTION_KEYS.FEATURED_CATEGORY_SHOWCASE]}
          />
        );

      case HUB_HOME_SECTION_KEYS.QUICK_BUY_REQUEST:
        return (
          <HubQuickBuyRequestSection
            key={sectionKey}
            categories={data?.product_categories}
          />
        );

      case HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES:
        return (
          <HubTopIndustriesSection
            key={sectionKey}
            industries={topIndustriesItems}
            loading={loading}
            displayConfig={sectionGridConfigs[HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES]}
          />
        );

      case HUB_HOME_SECTION_KEYS.CONTRACT_MANUFACTURERS:
        return (
          <HubContractManufacturersSection
            key={sectionKey}
            manufacturers={data?.contract_manufacturers}
            industries={data?.contract_manufacturer_industries}
            loading={loading}
            displayConfig={sectionGridConfigs[HUB_HOME_SECTION_KEYS.CONTRACT_MANUFACTURERS]}
          />
        );

      case HUB_HOME_SECTION_KEYS.ACTIVE_AUCTIONS:
        return (
          <HubActiveAuctionsSection
            key={sectionKey}
            auctions={data?.active_auctions}
            categories={data?.auction_categories}
            loading={loading}
            displayConfig={sectionGridConfigs[HUB_HOME_SECTION_KEYS.ACTIVE_AUCTIONS]}
          />
        );

      case HUB_HOME_SECTION_KEYS.INFRASTRUCTURE_LEASING: {
        const infraConfig = sectionGridConfigs[HUB_HOME_SECTION_KEYS.INFRASTRUCTURE_LEASING];
        const infraItems = (
          data?.infrastructure_services ||
          data?.services?.filter((s) => s.service_type === "infrastructure") ||
          []
        ).slice(0, infraConfig.total_items);

        return (
          <section key={sectionKey} id="hub-infrastructure-services" className="hub-section hub-section--infra">
            <SectionHeader
              title="Infrastructure Leasing"
              viewMoreTo={buildInfraBrowseHref()}
            />
            <div
              className="hub-card-grid"
              style={{ gridTemplateColumns: `repeat(${infraConfig.products_per_row}, minmax(0, 1fr))` }}
            >
              {infraItems.map((item) => (
                <HubInfrastructureServiceCard key={item.id} item={item} />
              ))}
              {!loading && !infraItems.length ? (
                <p className="hub-empty">No infrastructure listings yet.</p>
              ) : null}
            </div>
          </section>
        );
      }

      case HUB_HOME_SECTION_KEYS.PROMO_BANNERS:
        return promoBanners?.length ? (
          <div key={sectionKey} className="hub-section hub-section--banner-strip">
            <HubHomeBannerCarousel
              banners={promoBanners}
              bannerSlot="promo"
              ariaLabel="Bottom page banners"
            />
          </div>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="hub-home-page">
      <B2BHubHomeHeader />

      <main className="hub-home-main">
        <div className="container-fluid hub-home-main__inner">
          {visibleSectionKeys.map((sectionKey) => renderHomeSection(sectionKey))}
        </div>
      </main>

      <HubHomeFooter />

      <HubFloatingRail />
    </div>
  );
}
